import { log } from '../utils/logger';

/**
 * Payments API module
 * Handles all payment-related API operations with Stripe integration
 */

import { supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Payment API namespace
 */
export const paymentAPI = {
  /**
   * Create a payment intent for a reservation
   * @param {Object} data - Payment intent data
   * @param {string} data.reservationId - Reservation ID
   * @param {number} data.amount - Amount in dollars
   * @param {string} data.currency - Currency code (default: 'usd')
   * @param {string} data.paymentMethodId - Optional payment method ID
   * @param {Object} data.metadata - Optional metadata
   * @returns {Promise<APIResponse>}
   */
  createIntent: createAPIMethod(
    async (data) => {
      log.info('💳 Creating payment intent:', {
        reservationId: data.reservationId,
        amount: data.amount,
        currency: data.currency || 'usd'
      });

      // Get the current user's session to pass the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User must be authenticated to create payment intent');
      }

      const { data: result, error } = await supabase.functions.invoke('create-payment-intent', {
        body: data,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('❌ Payment intent creation failed:', error);
        // Try to extract more error details
        if (error.context?.body) {
          log.error('Error details:', error.context.body);
        }
        throw error;
      }
      
      // Check if result contains an error
      if (result?.error) {
        log.error('❌ Payment intent error from function:', result.error);
        throw new Error(result.error);
      }

      log.info('✅ Payment intent created:', {
        paymentIntentId: result.paymentIntentId,
        amount: result.amount
      });

      return { data: result, error: null };
    },
    { 
      operation: 'Create payment intent',
      transformData: (result) => {
        log.info('💳 Payment intent ready for confirmation');
        return result;
      }
    }
  ),

  /**
   * Confirm a payment intent
   * @param {Object} data - Payment confirmation data
   * @param {string} data.paymentIntentId - Payment Intent ID
   * @param {string} data.paymentMethodId - Optional payment method ID
   * @returns {Promise<APIResponse>}
   */
  confirmPayment: createAPIMethod(
    async (data) => {
      log.info('🔄 Confirming payment:', {
        paymentIntentId: data.paymentIntentId
      });

      const { data: result, error } = await supabase.functions.invoke('confirm-payment', {
        body: data
      });

      if (error) {
        log.error('❌ Payment confirmation failed:', error);
        throw error;
      }

      log.info('✅ Payment confirmation response:', {
        success: result.success,
        requiresAction: result.requiresAction,
        status: result.paymentIntent?.status
      });

      return { data: result, error: null };
    },
    { 
      operation: 'Confirm payment',
      transformData: (result) => {
        if (result.success) {
          log.info('🎉 Payment confirmed successfully');
        } else if (result.requiresAction) {
          log.info('🔐 Payment requires additional action');
        }
        return result;
      }
    }
  ),

  /**
   * Process a refund for a payment
   * @param {Object} data - Refund data
   * @param {string} data.paymentId - Payment ID
   * @param {number} data.amount - Optional refund amount in dollars (full refund if not specified)
   * @param {string} data.reason - Refund reason
   * @param {string} data.description - Optional description
   * @returns {Promise<APIResponse>}
   */
  processRefund: createAPIMethod(
    async (data) => {
      log.info('💰 Processing refund:', {
        paymentId: data.paymentId,
        amount: data.amount,
        reason: data.reason
      });

      const { data: result, error } = await supabase.functions.invoke('process-refund', {
        body: data
      });

      if (error) {
        log.error('❌ Refund processing failed:', error);
        throw error;
      }

      log.info('✅ Refund processed:', {
        refundId: result.refundId,
        amount: result.amount,
        status: result.status
      });

      return { data: result, error: null };
    },
    { 
      operation: 'Process refund',
      transformData: (result) => {
        log.info('💸 Refund initiated successfully');
        return result;
      }
    }
  ),

  /**
   * Get payment details by reservation ID
   * @param {string} reservationId - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  getByReservation: createAPIMethod(
    async (reservationId) => {
      return await supabase
        .from('payments')
        .select(`
          *,
          refunds (
            id,
            amount_cents,
            currency,
            reason,
            status,
            description,
            created_at
          )
        `)
        .eq('reservation_id', reservationId)
        .order('created_at', { ascending: false });
    },
    { operation: 'Get payment by reservation' }
  ),

  /**
   * Get payment details by payment ID
   * @param {string} paymentId - Payment ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (paymentId) => {
      return await supabase
        .from('payments')
        .select(`
          *,
          reservations (
            id,
            contact_name,
            contact_email,
            status,
            venues (name),
            teams (name)
          ),
          refunds (
            id,
            amount_cents,
            currency,
            reason,
            status,
            description,
            created_at,
            processed_by
          )
        `)
        .eq('id', paymentId)
        .single();
    },
    { operation: 'Get payment by ID' }
  ),

  /**
   * Get all payments for a team (team managers only)
   * @param {string} teamId - Team ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<APIResponse>}
   */
  getByTeam: createAPIMethod(
    async (teamId, filters = {}) => {
      let query = supabase
        .from('payments')
        .select(`
          *,
          reservations (
            id,
            contact_name,
            status,
            reserved_at,
            venues (name),
            teams!inner (id, name)
          )
        `)
        .eq('reservations.teams.id', teamId);

      // Apply filters
      if (filters.status) {
        query = query.eq('payment_status', filters.status);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      // Order by most recent
      query = query.order('created_at', { ascending: false });

      return await query;
    },
    { operation: 'Get payments by team' }
  ),

  /**
   * Get payment history for a user
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<APIResponse>}
   */
  getByUser: createAPIMethod(
    async (userId, filters = {}) => {
      let query = supabase
        .from('payments')
        .select(`
          *,
          reservations (
            id,
            contact_name,
            status,
            reserved_at,
            venues (name),
            teams (name)
          )
        `)
        .eq('created_by', userId);

      // Apply filters
      if (filters.status) {
        query = query.eq('payment_status', filters.status);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      // Order by most recent
      query = query.order('created_at', { ascending: false });

      return await query;
    },
    { operation: 'Get payments by user' }
  ),

  /**
   * Get refund details by refund ID
   * @param {string} refundId - Refund ID
   * @returns {Promise<APIResponse>}
   */
  getRefund: createAPIMethod(
    async (refundId) => {
      return await supabase
        .from('refunds')
        .select(`
          *,
          payments (
            id,
            amount_cents,
            currency,
            reservations (
              id,
              contact_name,
              venues (name),
              teams (name)
            )
          )
        `)
        .eq('id', refundId)
        .single();
    },
    { operation: 'Get refund details' }
  ),

  /**
   * Get all refunds for a team (team managers only)
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getRefundsByTeam: createAPIMethod(
    async (teamId) => {
      return await supabase
        .from('refunds')
        .select(`
          *,
          payments (
            id,
            amount_cents,
            currency,
            reservations (
              id,
              contact_name,
              venues (name),
              teams!inner (id, name)
            )
          )
        `)
        .eq('payments.reservations.teams.id', teamId)
        .order('created_at', { ascending: false });
    },
    { operation: 'Get refunds by team' }
  ),

  /**
   * Check if user can process payments for a reservation
   * @param {string} reservationId - Reservation ID
   * @returns {Promise<boolean>}
   */
  canProcessPayment: createAPIMethod(
    async (reservationId) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { data: false, error: null };

      // Get reservation details
      const { data: reservation, error } = await supabase
        .from('reservations')
        .select('team_id, created_by')
        .eq('id', reservationId)
        .single();

      if (error || !reservation) {
        return { data: false, error };
      }

      // Check if user created the reservation
      if (reservation.created_by === user.id) {
        return { data: true, error: null };
      }

      // Check if user is a team manager
      const { data: teamRole } = await supabase
        .from('user_team_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('team_id', reservation.team_id)
        .in('role', ['primary_manager', 'assistant_manager'])
        .single();

      return { data: Boolean(teamRole), error: null };
    },
    { operation: 'Check payment permissions' }
  )
};

/**
 * Admin Payment API namespace (TeamRezy employees only)
 */
export const adminPaymentAPI = {
  /**
   * Get all payments with filters (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAllPayments: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('payments')
        .select(`
          *,
          reservations (
            id,
            contact_name,
            contact_email,
            contact_phone,
            status,
            reserved_at,
            group_size,
            teams (id, name),
            venues (id, name, city, state)
          ),
          refunds (
            id,
            amount_cents,
            status,
            reason,
            created_at
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        query = query.eq('payment_status', filters.status);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      return await query;
    },
    { operation: 'Get all payments (admin)' }
  ),

  /**
   * Get payment analytics data (admin only)
   * @param {Object} params - Analytics parameters
   * @returns {Promise<APIResponse>}
   */
  getAnalytics: createAPIMethod(
    async ({ timeRange = '30days' }) => {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case '7days':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30days':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90days':
          startDate.setDate(endDate.getDate() - 90);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(endDate.getDate() - 30);
      }

      return await supabase
        .from('payments')
        .select(`
          *,
          reservations (
            teams (id, name),
            venues (id, name, city, state)
          ),
          refunds (
            amount_cents,
            reason,
            created_at
          )
        `)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });
    },
    { operation: 'Get payment analytics (admin)' }
  ),

  /**
   * Get refund-eligible payments (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getRefundEligiblePayments: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('payments')
        .select(`
          id,
          stripe_payment_intent_id,
          stripe_charge_id,
          amount_cents,
          captured_amount_cents,
          amount_refunded_cents,
          currency,
          payment_status,
          created_at,
          reservations (
            id,
            contact_name,
            contact_email,
            status,
            reserved_at,
            teams (id, name),
            venues (id, name, city, state)
          ),
          refunds (
            id,
            amount_cents,
            status
          )
        `)
        .in('payment_status', ['succeeded', 'captured', 'partially_refunded']) // Include succeeded, captured, and partially refunded payments
        .order('created_at', { ascending: false });

      // Apply date filters
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      return await query;
    },
    { operation: 'Get refund eligible payments (admin)' }
  ),

  /**
   * Export payments data (admin only)
   * @param {Object} config - Export configuration
   * @returns {Promise<APIResponse>}
   */
  exportPayments: createAPIMethod(
    async (config) => {
      let query = supabase
        .from('payments')
        .select(`
          id,
          stripe_payment_intent_id,
          stripe_charge_id,
          amount_cents,
          currency,
          payment_status,
          payment_method_type,
          receipt_url,
          failure_reason,
          created_at,
          updated_at,
          ${config.includeReservationData ? `
          reservations (
            id,
            contact_name,
            contact_email,
            contact_phone,
            status,
            reserved_at,
            duration_hours,
            group_size,
            special_requests,
            teams (id, name),
            venues (id, name, city, state, address_line1)
          ),` : ''}
          ${config.includeRefunds ? `
          refunds (
            id,
            stripe_refund_id,
            amount_cents,
            currency,
            reason,
            status,
            description,
            created_at
          )` : ''}
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (config.status !== 'all') {
        query = query.eq('payment_status', config.status);
      }
      if (config.dateFrom) {
        query = query.gte('created_at', config.dateFrom);
      }
      if (config.dateTo) {
        const endDate = new Date(config.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      return await query;
    },
    { operation: 'Export payments data (admin)' }
  ),

  /**
   * Process bulk refunds (admin only)
   * @param {Array} paymentIds - Array of payment IDs
   * @param {Object} refundConfig - Refund configuration
   * @returns {Promise<Array>}
   */
  processBulkRefunds: createAPIMethod(
    async (paymentIds, refundConfig) => {
      const results = [];

      for (const paymentId of paymentIds) {
        try {
          log.info(`Processing refund for payment ${paymentId}`);

          const { data: result, error } = await supabase.functions.invoke('process-refund', {
            body: {
              paymentId,
              amount: refundConfig.partial ? parseFloat(refundConfig.partialAmount) : undefined,
              reason: refundConfig.reason,
              description: `Bulk refund - ${refundConfig.description || 'Admin initiated'}`
            }
          });

          if (error) throw error;

          results.push({
            paymentId,
            status: 'success',
            refundId: result.refundId,
            amount: result.amount,
            message: 'Refund processed successfully'
          });

        } catch (err) {
          log.error(`❌ Refund failed for payment ${paymentId}:`, err);

          results.push({
            paymentId,
            status: 'error',
            message: err.message || 'Refund processing failed'
          });
        }
      }

      return { data: results, error: null };
    },
    { operation: 'Process bulk refunds (admin)' }
  ),

  /**
   * Capture a payment that has been authorized (admin only)
   * @param {Object} data - Capture data
   * @param {string} data.paymentIntentId - Stripe payment intent ID
   * @param {string} data.reservationId - Reservation ID
   * @param {number} data.amountToCapture - Optional amount to capture in cents
   * @returns {Promise<APIResponse>}
   */
  capturePayment: createAPIMethod(
    async (data) => {
      log.info('🎯 Capturing payment (admin):', {
        paymentIntentId: data.paymentIntentId,
        reservationId: data.reservationId,
        amountToCapture: data.amountToCapture
      });

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User must be authenticated to capture payment');
      }

      const { data: result, error } = await supabase.functions.invoke('admin-capture-payment', {
        body: data,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('❌ Payment capture failed:', error);
        throw error;
      }
      
      if (result?.error) {
        log.error('❌ Payment capture error from function:', result.error);
        throw new Error(result.error);
      }

      log.info('✅ Payment captured:', {
        paymentIntentId: result.paymentIntentId,
        capturedAmount: result.capturedAmount
      });

      return { data: result, error: null };
    },
    { 
      operation: 'Capture payment (admin)',
      transformData: (result) => {
        log.info('💰 Payment captured successfully');
        return result;
      }
    }
  ),

  /**
   * Cancel a payment that has been authorized (admin only)
   * @param {Object} data - Cancel data
   * @param {string} data.paymentIntentId - Stripe payment intent ID
   * @param {string} data.reservationId - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  cancelPayment: createAPIMethod(
    async (data) => {
      log.info('🚫 Canceling payment (admin):', {
        paymentIntentId: data.paymentIntentId,
        reservationId: data.reservationId
      });

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User must be authenticated to cancel payment');
      }

      const { data: result, error } = await supabase.functions.invoke('cancel-payment', {
        body: data,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        log.error('❌ Payment cancellation failed:', error);
        throw error;
      }
      
      if (result?.error) {
        log.error('❌ Payment cancellation error from function:', result.error);
        throw new Error(result.error);
      }

      log.info('✅ Payment canceled:', {
        paymentIntentId: result.paymentIntent?.id,
        status: result.paymentIntent?.status
      });

      return { data: result, error: null };
    },
    { 
      operation: 'Cancel payment (admin)',
      transformData: (result) => {
        log.info('🚫 Payment canceled successfully');
        return result;
      }
    }
  ),

  /**
   * Get payments awaiting capture (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getPaymentsAwaitingCapture: createAPIMethod(
    async (filters = {}) => {
      log.info('🔍 Fetching payments awaiting capture');

      let query = supabase
        .from('payments')
        .select(`
          id,
          stripe_payment_intent_id,
          amount_cents,
          currency,
          payment_status,
          created_at,
          updated_at,
          reservations (
            id,
            contact_name,
            contact_email,
            status,
            reserved_at,
            group_size,
            teams (id, name),
            venues (id, name, city, state, address_line1)
          )
        `)
        .eq('payment_status', 'requires_capture') // Authorized but not captured
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      const result = await query;
      
      if (result.data) {
        log.info(`✅ Found ${result.data.length} payments awaiting capture`);
      }

      return result;
    },
    { operation: 'Get payments awaiting capture (admin)' }
  )
};